/* Soft teacher-side chime when a student submits a quiz answer.
 * Global mute persisted in localStorage. WebAudio — no asset download. */
import { useEffect, useRef, useState } from "react";

const KEY = "answerChime.muted";
export const isChimeMuted = () => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } };
export const setChimeMuted = (m) => { try { localStorage.setItem(KEY, m ? "1" : "0"); } catch { /* noop */ } };

let ctx = null;
export function playAnswerChime() {
  if (isChimeMuted()) return;
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const t0 = ctx.currentTime;
    [[880, 0], [1318.5, 0.09]].forEach(([freq, dt]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t0 + dt);
      g.gain.exponentialRampToValueAtTime(0.07, t0 + dt + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.5);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t0 + dt);
      o.stop(t0 + dt + 0.55);
    });
  } catch { /* audio unavailable */ }
}

/* Chimes when a NEW response starts or a response is submitted (never on
 * initial load, never when switching quizzes via resetKey). */
export function useAnswerChime(responses, resetKey) {
  const prevRef = useRef(null);
  const keyRef = useRef(resetKey);
  useEffect(() => {
    if (keyRef.current !== resetKey) { keyRef.current = resetKey; prevRef.current = null; }
    const list = responses || [];
    const sig = list.length + list.filter((r) => r.status === "final").length;
    if (prevRef.current === null) { prevRef.current = sig; return; }
    if (sig > prevRef.current) playAnswerChime();
    prevRef.current = sig;
  }, [responses, resetKey]);
}

export function useChimeMute() {
  const [muted, setMuted] = useState(isChimeMuted());
  const toggle = () => setMuted((m) => { setChimeMuted(!m); return !m; });
  return [muted, toggle];
}
